import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Link,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";

import { RootState } from "../../app/rootReducer";
import { formatCommentTimestamp } from "./commentFormatting";
import {
  fetchProgramTicketDetails,
  ProgramTicketDetailsStatus,
} from "./programTicketsConnector";
import { ProgramTicket, ProgramTicketDetails, TicketComment } from "./types";

export const TicketDetailsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const selectedTicketIds = useSelector(
    (state: RootState) => state.programTickets.selectedTicketIds
  );
  const list = useSelector((state: RootState) => state.programTickets.list);
  const detailsById = useSelector(
    (state: RootState) => state.programTickets.detailsById
  );
  const detailsStatus = useSelector(
    (state: RootState) => state.programTickets.detailsStatus
  );
  const detailsError = useSelector(
    (state: RootState) => state.programTickets.detailsError
  );

  // One AbortController per in-flight details fetch. When a ticket is
  // deselected we cancel its fetch (if still running) and drop the entry;
  // when a new ticket is selected we spin up a fresh controller if the
  // ticket's details are not already cached.
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    const activeIds = new Set(selectedTicketIds);

    for (const [ticketId, controller] of controllersRef.current) {
      if (!activeIds.has(ticketId)) {
        controller.abort();
        controllersRef.current.delete(ticketId);
      }
    }

    for (const ticketId of selectedTicketIds) {
      if (controllersRef.current.has(ticketId)) continue;
      const cached = store.getState().programTickets.detailsById[ticketId];
      if (cached !== undefined) continue;
      spawnDetailsFetch(ticketId);
    }
  }, [selectedTicketIds, dispatch, store]);

  // Drop the ref entry once a fetch settles so a subsequent selection of
  // the same ticket can re-fetch. Without this, an errored ticket keeps
  // its stale controller in the map — the `has(ticketId)` guard above
  // would then short-circuit re-fetches on re-selection and leave Retry
  // as the only recovery path.
  function spawnDetailsFetch(ticketId: string): void {
    const controller = new AbortController();
    controllersRef.current.set(ticketId, controller);
    const cleanup = () => {
      if (controllersRef.current.get(ticketId) === controller) {
        controllersRef.current.delete(ticketId);
      }
    };
    const dispatched = dispatch(
      fetchProgramTicketDetails({
        ticketId,
        signal: controller.signal,
      }) as unknown as ReturnType<typeof fetchProgramTicketDetails>
    ) as unknown as Promise<unknown> | undefined;
    if (
      dispatched &&
      typeof (dispatched as Promise<unknown>).then === "function"
    ) {
      (dispatched as Promise<unknown>).then(cleanup, cleanup);
    }
  }

  useEffect(() => {
    return () => {
      for (const controller of controllersRef.current.values()) {
        controller.abort();
      }
      controllersRef.current.clear();
    };
  }, []);

  const handleRetry = useCallback(
    (ticketId: string) => {
      const existing = controllersRef.current.get(ticketId);
      if (existing !== undefined) {
        existing.abort();
        controllersRef.current.delete(ticketId);
      }
      spawnDetailsFetch(ticketId);
    },
    [dispatch]
  );

  if (selectedTicketIds.length === 0) {
    return null;
  }

  return (
    <SpaceBetween size="l">
      <Header>{`Ticket Details (${selectedTicketIds.length} selected)`}</Header>
      {selectedTicketIds.map((ticketId) => (
        <TicketDetailsBlock
          key={ticketId}
          ticketId={ticketId}
          row={list.find((r) => r.ticketId === ticketId)}
          details={detailsById[ticketId]}
          status={detailsStatus[ticketId]}
          error={detailsError[ticketId]}
          onRetry={handleRetry}
        />
      ))}
    </SpaceBetween>
  );
};

interface TicketDetailsBlockProps {
  readonly ticketId: string;
  readonly row: ProgramTicket | undefined;
  readonly details: ProgramTicketDetails | undefined;
  readonly status: ProgramTicketDetailsStatus | undefined;
  readonly error: string | undefined;
  readonly onRetry: (ticketId: string) => void;
}

const TicketDetailsBlock: React.FC<TicketDetailsBlockProps> = ({
  ticketId,
  row,
  details,
  status,
  error,
  onRetry,
}) => {
  return (
    <Container
      header={row !== undefined ? <TicketHeaderRow row={row} /> : undefined}
    >
      {renderBody({ ticketId, status, details, error, onRetry })}
    </Container>
  );
};

interface BodyProps {
  readonly ticketId: string;
  readonly status: ProgramTicketDetailsStatus | undefined;
  readonly details: ProgramTicketDetails | undefined;
  readonly error: string | undefined;
  readonly onRetry: (ticketId: string) => void;
}

function renderBody({
  ticketId,
  status,
  details,
  error,
  onRetry,
}: BodyProps): React.ReactElement {
  if (status === "error") {
    return (
      <Alert
        type="error"
        header={`Could not load ticket details for ${ticketId}`}
      >
        <SpaceBetween size="s">
          {error !== undefined ? <Box variant="p">{error}</Box> : null}
          <Button onClick={() => onRetry(ticketId)}>Retry</Button>
        </SpaceBetween>
      </Alert>
    );
  }
  if (status === "loading" || details === undefined) {
    return <Spinner />;
  }
  return <DetailsBody details={details} />;
}

interface DetailsBodyProps {
  readonly details: ProgramTicketDetails;
}

const DetailsBody: React.FC<DetailsBodyProps> = ({ details }) => {
  return (
    <SpaceBetween size="m">
      <Box variant="p">{details.description}</Box>
      <RecentComments comments={details.comments} />
    </SpaceBetween>
  );
};

interface TicketHeaderRowProps {
  readonly row: ProgramTicket;
}

const TicketHeaderRow: React.FC<TicketHeaderRowProps> = ({ row }) => {
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Link
        external
        href={row.externalUrl}
        externalIconAriaLabel={`Opens ${row.ticketId} in SIM-T`}
      >
        {row.ticketId}
      </Link>
      <Box>{row.title}</Box>
    </SpaceBetween>
  );
};

interface RecentCommentsProps {
  readonly comments: readonly TicketComment[];
}

const RecentComments: React.FC<RecentCommentsProps> = ({ comments }) => {
  return (
    <SpaceBetween size="s">
      <Header variant="h3">Recent Comments</Header>
      {comments.length === 0 ? (
        <Box>No comments yet.</Box>
      ) : (
        <SpaceBetween size="s">
          {comments.map((comment) => (
            <CommentBlock key={comment.commentId} comment={comment} />
          ))}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

interface CommentBlockProps {
  readonly comment: TicketComment;
}

const CommentBlock: React.FC<CommentBlockProps> = ({ comment }) => {
  return (
    <SpaceBetween size="xxs">
      <Box>
        @{comment.authorHandle} · {formatCommentTimestamp(comment.createdAt)}
      </Box>
      <Box variant="p">{comment.body}</Box>
    </SpaceBetween>
  );
};

export default TicketDetailsPanel;
